import { useState, useRef } from "react";
import type { Initiative } from "@/types";
import { StatusDot } from "@/components/ui/Badge";
import { ICON_MAP } from "@/components/modals/IconPicker";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";
import { formatDateRange } from "@/utils/dateRange";

/**
 * A calendar event rendered as a compact pill matching the Timeline card's visual language:
 * classification color as the left accent + subtle tint, title, status dot, tag icons, star,
 * and (for date-mode initiatives) the exact start/end date range — mirroring the Timeline pill.
 */
export function CalendarEvent({ event }: { event: { resource: Initiative } }) {
  const initiative = event.resource as any;
  const classification = initiative.classification;
  const status = initiative.status;
  const color = classification?.color || "#3B82F6";
  const isStarred = initiative.isStarred === true;

  const titleRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const displayTags =
    initiative.tags?.filter((t: any) => t.tag?.displayAsIcon && t.tag?.icon) || [];

  // Date-mode initiatives show their exact start/end dates (month-mode: nothing).
  const dateRange =
    initiative.datePrecision === 'day'
      ? formatDateRange(initiative.startDate, initiative.endDate)
      : null;

  const handleTitleMouseEnter = () => {
    const el = titleRef.current;
    if (el && el.scrollWidth > el.clientWidth) setShowTooltip(true);
    else if (dateRange) setShowTooltip(true);
  };

  return (
    <div
      className="group relative flex items-center gap-1 h-full w-full px-1.5 rounded overflow-hidden"
      style={{
        backgroundColor: `${color}1A`,
        borderLeft: `3px solid ${color}`,
      }}
    >
      {status && <StatusDot color={status.color} />}
      <span
        ref={titleRef}
        className="text-xs font-medium text-text-primary truncate min-w-0 flex-1"
        onMouseEnter={handleTitleMouseEnter}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {initiative.title}
      </span>

      {dateRange && (
        <span className="text-[10px] text-text-secondary whitespace-nowrap flex-shrink-0">
          {dateRange}
        </span>
      )}

      {isStarred && <StarSolid className="w-3 h-3 text-amber-400 flex-shrink-0" />}

      {displayTags.slice(0, 2).map((initTag: any) => {
        const IconComponent = initTag.tag?.icon ? ICON_MAP[initTag.tag.icon] : null;
        if (!IconComponent) return null;
        return <IconComponent key={initTag.tagId} className="w-3 h-3 text-text-secondary flex-shrink-0" />;
      })}

      {showTooltip && (
        <div className="absolute left-0 top-full mt-1 z-[100] px-2 py-1 text-xs bg-gray-900 text-white rounded shadow-lg max-w-xs whitespace-normal">
          {initiative.title}
          {dateRange && <span className="text-gray-300"> · {dateRange}</span>}
        </div>
      )}
    </div>
  );
}
